import { useContext, useEffect, useState } from 'react'
import { Outlet, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Download, Upload } from '@factorialco/f0-react/icons/app'
import {
  AccountProviderConfigurationFragment,
  AccountTransactionsDataCollectionDocument,
  BankingTransactionFieldsFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { AccountTransactionsInsights } from 'modules/banking/components/AccountTransactionsInsights'
import { AccountActionsContext } from 'modules/banking/contexts/AccountActionsContext'
import { ImportTransactionsContext } from 'modules/banking/contexts/ImportTransactionsContext'
import { ProviderConfigurationContext } from 'modules/banking/contexts/ProviderConfigurationContext'

import { useAccountTransactionsDataCollection } from './useAccountTransactionsDataCollection'

const EmptyBlock = ({
  providerConfiguration,
}: {
  providerConfiguration: AccountProviderConfigurationFragment | null | undefined
}) => {
  const bankAggregation = providerConfiguration?.services.includes('bank_aggregation')
  const title = bankAggregation
    ? i18n.t('banking.accounts.importing_transactions_title')
    : undefined
  const description = bankAggregation
    ? i18n.t('banking.accounts.importing_transactions_content')
    : i18n.t('banking.accounts.transactions_empty_message')

  return {
    title: title || '',
    description,
  }
}

const AccountTransactionsDataTable = () => {
  const {
    access: { locale },
  } = useCurrentSession()
  const { navigation } = useNavigation()
  const { id: bankAccountId } = useParams<{
    id: string
  }>()
  const { newNumTransactions } = useContext(ImportTransactionsContext)
  const { reconcileCTA, importTransactionsCTA, exportTransactionsCTA, currency } =
    useContext(AccountActionsContext)
  const { configuration } = useContext(ProviderConfigurationContext)
  const [refreshKey, setRefreshKey] = useState(0)
  const [currentSearch, setCurrentSearch] = useState<string>('')
  const [dateFilters, setDateFilters] = useState<{ from?: Date; to?: Date }>({})
  const [typeFilters, setTypeFilters] = useState<string[]>([])
  const [cardSourceFilters, setCardSourceFilters] = useState<string[]>([])

  const { sortings, defaultSortings, columns } = useAccountTransactionsDataCollection({
    locale,
    bankAccountId: bankAccountId || '',
  })

  const buildFiltersObject = () => {
    const filters: Record<string, { in: string[] }> = {}
    if (typeFilters.length > 0) {
      filters.type = { in: typeFilters }
    }
    if (cardSourceFilters.length > 0) {
      filters.cardCreationSource = { in: cardSourceFilters }
    }
    return Object.keys(filters).length > 0 ? filters : undefined
  }

  const dataSource = useGraphqlDataCollectionSource(
    {
      document: AccountTransactionsDataCollectionDocument,
      mapData: (data) =>
        data.banking.transactionsConnection.edges.map(
          (edge: { node: BankingTransactionFieldsFragment }) => edge.node
        ),
      mapVariables: ({ search }) => ({
        accountIds: bankAccountId ? [Number(bankAccountId)] : [],
        status: 'done' as const,
        description: search || undefined,
        filters: buildFiltersObject(),
        from: dateFilters.from
          ? moment(dateFilters.from).startOf('day').format('YYYY-MM-DDTHH:mm:ss')
          : undefined,
        to: dateFilters.to
          ? moment(dateFilters.to).endOf('day').format('YYYY-MM-DDTHH:mm:ss')
          : undefined,
      }),
      paginationType: 'pages',
      perPage: 10,
      mapPagination: (data) => ({
        total: data.banking.transactionsConnection.totalCount ?? 0,
        hasNextPage: data.banking.transactionsConnection.pageInfo.hasNextPage ?? false,
        cursor: data.banking.transactionsConnection.pageInfo.endCursor ?? undefined,
      }),
      sortings,
      defaultSortings,
      search: {
        enabled: true,
      },
      filters: {
        bookingDate: {
          label: i18n.t('banking.attributes.booking_date'),
          type: 'date',
          options: {
            mode: 'range',
          },
        },
        type: {
          label: i18n.t('banking.attributes.type'),
          type: 'in',
          options: {
            options: [
              { label: i18n.t('banking.accounts.transaction_type.funding'), value: 'topup' },
              {
                label: i18n.t('banking.accounts.transaction_type.card_payment'),
                value: 'card_payment',
              },
              { label: i18n.t('banking.accounts.transaction_type.dispute'), value: 'dispute' },
              { label: i18n.t('banking.accounts.transaction_type.incoming'), value: 'incoming' },
              {
                label: i18n.t('banking.accounts.transaction_type.card_refund'),
                value: 'card_refund',
              },
              { label: i18n.t('banking.accounts.transaction_type.outgoing'), value: 'outgoing' },
            ],
          },
        },
        cardCreationSource: {
          label: i18n.t('expenses_v2.cards.table.creation_source'),
          type: 'in',
          options: {
            options: [
              { label: i18n.t('banking.cards.creation_source.expenses'), value: 'expenses' },
              { label: i18n.t('banking.cards.creation_source.software'), value: 'software' },
              {
                label: i18n.t('banking.accounts.card_creation_source.procurement'),
                value: 'procurement',
              },
              { label: i18n.t('banking.accounts.card_creation_source.benefits'), value: 'salary' },
            ],
          },
        },
      },
      itemUrl: (transaction: BankingTransactionFieldsFragment) =>
        navigation.finance.banking.accounts.accountId.transactions.transactionId.fullPath({
          id: bankAccountId || '',
          transaction_id: transaction.id.toString(),
        }),
      primaryActions: reconcileCTA
        ? () => [
            {
              label: i18n.t('banking.accounts.actions.reconcile'),
              onClick: () => {
                track(trackingEvents.accounting.banking.clickGoToReconcileView)
                reconcileCTA()
              },
              type: 'primary',
              icon: undefined,
            },
          ]
        : undefined,
      secondaryActions: {
        expanded: 0,
        actions: () => {
          const actions = []
          if (importTransactionsCTA) {
            actions.push({
              label: i18n.t('banking.accounts.actions.import_transactions'),
              icon: Upload,
              onClick: importTransactionsCTA,
            })
          }
          if (exportTransactionsCTA) {
            actions.push({
              label: i18n.t('banking.accounts.actions.export_transactions'),
              icon: Download,
              onClick: exportTransactionsCTA,
            })
          }

          return actions
        },
      },
    },
    [
      bankAccountId,
      refreshKey,
      navigation,
      reconcileCTA,
      exportTransactionsCTA,
      importTransactionsCTA,
      dateFilters,
      typeFilters,
      cardSourceFilters,
    ]
  )

  useEffect(() => {
    if (newNumTransactions != null) {
      setRefreshKey((old) => old + 1)
    }
  }, [newNumTransactions])

  const emptyState = EmptyBlock({ providerConfiguration: configuration })

  return (
    <>
      <Outlet />
      <Box gap="s16">
        {currency && (
          <AccountTransactionsInsights
            locale={locale}
            currency={currency}
            accountIds={[Number(bankAccountId)]}
            description={currentSearch}
            dateFrom={dateFilters.from}
            dateTo={dateFilters.to}
            additionalFilters={buildFiltersObject()}
          />
        )}

        <Box height="full">
          <OneDataCollection
            storage={{
              features: ['filters', 'sortings', 'search'],
            }}
            fullHeight
            emptyStates={{
              'no-data': emptyState,
            }}
            source={dataSource}
            onStateChange={(state) => {
              if (state.search !== currentSearch) {
                setCurrentSearch(state.search || '')
              }

              const bookingDateFilter = state.filters?.bookingDate
              if (bookingDateFilter !== undefined) {
                const newDateFilters: { from?: Date; to?: Date } = {}

                if (bookingDateFilter instanceof Date) {
                  newDateFilters.from = bookingDateFilter
                  newDateFilters.to = bookingDateFilter
                } else if (
                  bookingDateFilter &&
                  typeof bookingDateFilter === 'object' &&
                  'from' in bookingDateFilter &&
                  ('from' in bookingDateFilter || 'to' in bookingDateFilter)
                ) {
                  const dateRange = bookingDateFilter
                  if ('from' in dateRange) {
                    const fromValue = dateRange.from
                    newDateFilters.from =
                      fromValue instanceof Date
                        ? fromValue
                        : typeof fromValue === 'string'
                          ? new Date(fromValue)
                          : undefined
                  }
                  if ('to' in dateRange) {
                    const toValue = dateRange.to
                    newDateFilters.to =
                      toValue instanceof Date
                        ? toValue
                        : typeof toValue === 'string'
                          ? new Date(toValue)
                          : undefined
                  }
                }

                if (
                  newDateFilters.from?.getTime() !== dateFilters.from?.getTime() ||
                  newDateFilters.to?.getTime() !== dateFilters.to?.getTime()
                ) {
                  setDateFilters(newDateFilters)
                }
              } else if (dateFilters.from || dateFilters.to) {
                setDateFilters({})
              }

              const typeFilter = state.filters?.type
              if (
                Array.isArray(typeFilter) &&
                typeFilter.every((item): item is string => typeof item === 'string') &&
                JSON.stringify(typeFilter) !== JSON.stringify(typeFilters)
              ) {
                setTypeFilters(typeFilter)
              } else if (!typeFilter && typeFilters.length > 0) {
                setTypeFilters([])
              }

              const cardSourceFilter = state.filters?.cardCreationSource
              if (
                Array.isArray(cardSourceFilter) &&
                cardSourceFilter.every((item): item is string => typeof item === 'string') &&
                JSON.stringify(cardSourceFilter) !== JSON.stringify(cardSourceFilters)
              ) {
                setCardSourceFilters(cardSourceFilter)
              } else if (!cardSourceFilter && cardSourceFilters.length > 0) {
                setCardSourceFilters([])
              }
            }}
            visualizations={[
              {
                type: 'table',
                options: {
                  allowColumnHiding: true,
                  allowColumnReordering: true,
                  columns,
                },
              },
            ]}
          />
        </Box>
      </Box>
    </>
  )
}

export default AccountTransactionsDataTable
